import React, { useState, useEffect } from "react";
import "./styles.css";
import { useLocalization } from "../../context/LocalizationContext";
import { convertToArabicNumerals } from "../../utils/helpers";
import Button from "../../components/shared/Button";
import { type Question, type QuestionOption, QuestionType } from "../../dtos/questionnaire";

interface AssessmentQuestionProps {
  question: Question;
  questionIndex: number;
  totalQuestions: number;
  onAnswer: (questionId: number, answer: number | number[]) => void;
  currentAnswer: number | number[] | undefined;
}

const isExclusiveOption = (option: QuestionOption): boolean => {
  const text = option.displayText.toLowerCase();
  return (
    text.includes("none") ||
    text.includes("other") ||
    text.includes("لا شيء") ||
    text.includes("أخرى")
  );
};

const AssessmentQuestion: React.FC<AssessmentQuestionProps> = ({
  question,
  questionIndex,
  totalQuestions,
  onAnswer,
  currentAnswer,
}) => {
  const { isRTL, t } = useLocalization();

  const [selectedOptions, setSelectedOptions] = useState<number | number[] | null>(() => {
    if (question.question_type === QuestionType.MULTI_SELECT) {
      return Array.isArray(currentAnswer) ? currentAnswer : [];
    }
    return currentAnswer ?? null;
  });

  useEffect(() => {
    if (question.question_type === QuestionType.MULTI_SELECT) {
      setSelectedOptions(Array.isArray(currentAnswer) ? currentAnswer : []);
    } else {
      setSelectedOptions(currentAnswer ?? null);
    }
  }, [currentAnswer, question.question_type]);

  const handleOptionClick = (option: QuestionOption): void => {
    if (question.question_type === QuestionType.SINGLE_CHOICE) {
      setSelectedOptions(option.id);
      onAnswer(question.id, option.id);
      return;
    }

    if (question.question_type === QuestionType.MULTI_SELECT) {
      const current = Array.isArray(selectedOptions) ? selectedOptions : [];

      let newSelection: number[];

      if (isExclusiveOption(option)) {
        newSelection = current.includes(option.id)
          ? current.filter((id) => id !== option.id)
          : [option.id];
      } else {
        const exclusiveSelected = question.options.find(
          (opt) => isExclusiveOption(opt) && current.includes(opt.id)
        );
        const filtered = exclusiveSelected
          ? current.filter((id) => id !== exclusiveSelected.id)
          : current;

        newSelection = filtered.includes(option.id)
          ? filtered.filter((id) => id !== option.id)
          : [...filtered, option.id];
      }

      setSelectedOptions(newSelection);
      onAnswer(question.id, newSelection);
      return;
    }
  };

  const isOptionSelected = (optionId: number): boolean => {
    if (question.question_type === QuestionType.SINGLE_CHOICE) {
      return selectedOptions === optionId;
    }
    if (question.question_type === QuestionType.MULTI_SELECT) {
      return Array.isArray(selectedOptions) && selectedOptions.includes(optionId);
    }
    return false;
  };

  const sortedOptions = [...question.options].sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <div className="question-container">
      <div className="question-header">
        <span className="question-counter">
          {t("question")}{" "}
          {isRTL ? convertToArabicNumerals(questionIndex + 1) : questionIndex + 1}{" "}
          {t("outOf")}{" "}
          {isRTL ? convertToArabicNumerals(totalQuestions) : totalQuestions}
        </span>
      </div>

      <div className="question-content">
        <h2 className="question-text">{question.text}</h2>

        <div className="options-container">
          {sortedOptions.map((option) => (
            <Button
              key={option.id}
              variant="card"
              className={`assessment-item ${isOptionSelected(option.id) ? "selected" : ""}`}
              onClick={() => handleOptionClick(option)}
              aria-pressed={isOptionSelected(option.id)}
            >
              <div className="assessment-info">{option.displayText}</div>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AssessmentQuestion;
